import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowRight, Info, Lightbulb, Wrench, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { setDraperPrefill } from "@/lib/draper-prefill";
import { useWorkerStatus } from "@/hooks/use-worker-status";
import type { AiEngine } from "@/lib/ai-engine";
import { SectionHeading } from "./item-ui";
import type { AdvancedToolLink } from "./simple-studio";

const STARTERS = [
  { label: "Write this week's content", text: "Write this week's content." },
  { label: "Draft a LinkedIn post about …", text: "Draft a LinkedIn post about " },
  { label: "Plan a campaign for …", text: "Plan a campaign for " },
  { label: "What should I approve?", text: "What should I approve?" },
];

interface QueueRow {
  id: number;
  title: string;
  status?: string | null;
  focusKeyword?: string | null;
  notes?: string | null;
}

const TOOL_NAME = { pages: "Page Builder", videos: "Video Studio" } as const;

/** Ask Draper (hands the text to the Draper chat) plus the read-only topic backlog. */
export function IdeasTab({
  workspaceId,
  engine,
  advancedTool = null,
  onDismissAdvancedTool,
}: {
  workspaceId: number;
  engine: AiEngine;
  /** Set when an old link pointed at the Page Builder or Video Studio. */
  advancedTool?: AdvancedToolLink | null;
  onDismissAdvancedTool?: () => void;
}) {
  const [, navigate] = useLocation();
  const [text, setText] = useState("");
  const [showAllTopics, setShowAllTopics] = useState(false);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const worker = useWorkerStatus(workspaceId, engine);

  const { data: queue } = useQuery<QueueRow[]>({
    queryKey: ["/api/workspaces", workspaceId, "content-queue"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${workspaceId}/content-queue`, { credentials: "include" });
      if (!res.ok) return [];
      const rows = await res.json();
      return Array.isArray(rows) ? rows : [];
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
  const topics = (queue || []).filter((q) => q.status === "pending");

  const send = (message: string) => {
    const trimmed = message.trim();
    if (!trimmed) return;
    setDraperPrefill(trimmed);
    navigate("/draper");
  };

  const fill = (value: string) => {
    setText(value);
    requestAnimationFrame(() => {
      const el = boxRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(value.length, value.length);
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8" data-testid="ideas-tab">
      {advancedTool && (
        <div className="space-y-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100 sm:p-4" role="note" data-testid="advanced-tool-notice">
          <p className="flex items-start gap-2">
            <Wrench size={15} className="mt-0.5 shrink-0" />
            <span>
              That link opens the {TOOL_NAME[advancedTool.tool]}, one of the advanced tools. They use the built-in writer
              {advancedTool.tool === "videos" ? " and can spend video credits" : ""}. Draper's team can do the work for you instead.
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2 pl-6">
            {advancedTool.tool === "videos" && advancedTool.script && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fill(`Make a short video from this script:\n\n${advancedTool.script!.slice(0, 3500)}`)}
                data-testid="button-ask-draper-video"
              >
                Ask Draper to make this video
              </Button>
            )}
            <a href={advancedTool.href} className="text-sm underline underline-offset-2" data-testid="link-open-advanced-tool">
              Open the advanced {TOOL_NAME[advancedTool.tool]}
            </a>
            {onDismissAdvancedTool && (
              <button type="button" onClick={onDismissAdvancedTool} className="ml-auto inline-flex items-center gap-1 text-xs text-amber-200/80 hover:text-amber-100" data-testid="button-dismiss-advanced-tool">
                <X size={12} /> Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      <section className="space-y-3" data-testid="ask-draper">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Ask Draper</h2>
          <p className="text-sm text-muted-foreground">
            {engine === "hybrid"
              ? "Tell Draper what you need. Draper answers right away and hands the writing to the Claude team; the draft shows up under \"Needs your review\"."
              : "Tell Draper what you need. The team writes it, and the draft shows up under \"Needs your review\"."}
          </p>
        </div>

        {worker.shouldWarn && (
          <p className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="worker-note">
            <Info size={14} className="mt-0.5 shrink-0" />
            Draper's writers haven't checked in recently, so a request may take longer than usual.
          </p>
        )}

        <div className="flex flex-wrap gap-2" role="group" aria-label="Starter requests">
          {STARTERS.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => fill(s.text)}
              className="rounded-full border border-border px-3 py-1.5 text-sm text-foreground/90 transition-colors hover:border-blue-500/50 hover:text-foreground"
              data-testid={`chip-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <Textarea
          ref={boxRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={4000}
          placeholder="For example: Write a short LinkedIn post about the October 15 GST/HST filing deadline."
          aria-label="What do you want Draper to do?"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(text);
          }}
          data-testid="input-ask-draper"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">This opens the Draper chat with your request filled in. You press send there.</p>
          <Button type="button" disabled={!text.trim()} onClick={() => send(text)} data-testid="button-ask-draper">
            Continue in Draper <ArrowRight size={14} />
          </Button>
        </div>
      </section>

      {topics.length > 0 && (
        <section className="space-y-3" data-testid="topic-ideas">
          <SectionHeading title="Topic ideas" count={topics.length} hint="Waiting to be written" />
          <div className="space-y-2">
            {(showAllTopics ? topics : topics.slice(0, 6)).map((t) => (
              <div key={t.id} className="flex flex-col gap-2 rounded-xl border border-border bg-card/60 p-3 sm:flex-row sm:items-center sm:p-4">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <Lightbulb size={16} className="mt-0.5 shrink-0 text-amber-300" />
                  <div className="min-w-0">
                    <p className="break-words text-sm font-medium text-foreground">{t.title}</p>
                    {t.focusKeyword && <p className="text-xs text-muted-foreground">Keyword: {t.focusKeyword}</p>}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fill(`Write an article about: ${t.title}`)}
                  className="w-full sm:w-auto"
                >
                  Ask Draper to write this
                </Button>
              </div>
            ))}
            {topics.length > 6 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowAllTopics((v) => !v)}>
                {showAllTopics ? "Show fewer" : `Show ${topics.length - 6} more`}
              </Button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
